/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import type { ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';

import { cssMap, cx, jsx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';

import { fg } from '@confluence/feature-gating';
import { getLogger } from '@confluence/logger';
import { useEditPageLoadingActions } from '@confluence/editor-loading-context/entry-points/EditPageLoadingContext.ts';

const logger = getLogger('LivePagePreloader');

type LivePagePreloaderProps = {
	isLivePage: boolean;
	children: React.ReactNode;
};

export const LivePagePreloader = ({
	isLivePage,
	children,
}: LivePagePreloaderProps):
	| string
	| number
	| boolean
	| Iterable<ReactNode>
	| JSX.Element
	| null
	| undefined => {
	const { livePageComponentsPreloader } = useEditPageLoadingActions();
	const [isPreloading, setIsPreloading] = useState<boolean>(false);

	useEffect(() => {
		if (!isPreloading || !isLivePage) return;

		const preloadComponents = () => {
			void livePageComponentsPreloader?.()
				.catch((error: Error) => {
					logger.error`Preloading live page components failed ${error}`;
				})
				.finally(() => {
					setIsPreloading(false);
				});
		};

		void preloadComponents();
	}, [isPreloading, isLivePage, livePageComponentsPreloader]);

	const triggerPreload = useCallback(() => {
		if (isLivePage && !isPreloading) {
			setIsPreloading(true);
		}
	}, [isLivePage, isPreloading]);

	if (!isLivePage || !fg('confluence_perf_preload_livepage_assets')) {
		return children;
	}

	return (
		<Box
			// presentation role here to tell screen readers that this is not interactive
			role="presentation"
			onMouseEnter={triggerPreload}
			onFocus={triggerPreload}
			xcss={cx(styles.box)}
		>
			{children}
		</Box>
	);
};

const styles = cssMap({
	box: {
		height: '100%',
		width: '100%',
	},
});
